import { DateTime, Duration } from "luxon";

import type { GetComputedItinerariesResponse } from "@upclaim/sdk/src/data-contracts";

import type { InitIncidentPayload } from "../../api/use-claim-actions";
import type { IncidentCause, IncidentConsequence } from "../ai-insights/ai-incident-preview";

/**
 * Pure logic extracted from InitIncident.vue (manual incident editor):
 * itinerary grouping, formatting helpers and the postIncident payload builder.
 */

export type ComputedItineraries = GetComputedItinerariesResponse;
export type ComputedItineraryEntry = ComputedItineraries["original"][number];
export type ComputedItineraryFlight = ComputedItineraryEntry["flight"];

export type FlightSelectionKey = "impactedFlight" | "faultyFlight" | "jurisdictionalFlight";
export type SelectedFlights = Partial<Record<FlightSelectionKey, ComputedItineraryFlight>>;

/** Mirrors groupedOriginal / groupedActual (Vue): impacted-sequence entries grouped by sequence. */
export function groupImpactedBySequence(
  entries: ComputedItineraryEntry[] | undefined,
): Array<[number, ComputedItineraryEntry[]]> {
  const groups = new Map<number, ComputedItineraryEntry[]>();
  for (const entry of entries ?? []) {
    if (!entry.isImpactedSequence) {
      continue;
    }
    const group = groups.get(entry.sequence) ?? [];
    group.push(entry);
    groups.set(entry.sequence, group);
  }
  return [...groups.entries()].sort(([a], [b]) => a - b);
}

export function getSequenceLabel(sequenceNum: number): string {
  return sequenceNum === 1 ? "Aller" : "Retour";
}

/** Human-readable delta ("1 heure, 30 minutes") — mirrors displayDelta (Vue). */
export function displayDelta(deltaInMinutes: number): string {
  const duration = Duration.fromObject({ minutes: deltaInMinutes }, { locale: "fr" }).shiftTo(
    "hours",
    "minutes",
  );
  return duration.toHuman({ unitDisplay: "long" });
}

/** Time in the airport's timezone (HH:mm) — mirrors formatTime (Vue). */
export function formatTime(dateString: Date | string | null | undefined, timeZone = "UTC"): string {
  if (!dateString) {
    return "—";
  }
  const dt = DateTime.fromISO(String(dateString), { zone: timeZone });
  return dt.isValid ? dt.toFormat("HH:mm") : "—";
}

/** Date in the airport's timezone (d LLL yyyy, fr) — mirrors formatDate (Vue). */
export function formatDate(dateString: Date | string | null | undefined, timeZone = "UTC"): string {
  if (!dateString) {
    return "—";
  }
  const dt = DateTime.fromISO(String(dateString), { zone: timeZone });
  return dt.isValid ? dt.setLocale("fr").toFormat("d LLL yyyy") : "—";
}

/** "Xh Ym" between two instants — mirrors calculateDuration (Vue). */
export function calculateDuration(
  departure: Date | string | null | undefined,
  arrival: Date | string | null | undefined,
): string {
  if (!departure || !arrival) {
    return "—";
  }
  const diff = new Date(String(arrival)).getTime() - new Date(String(departure)).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours}h ${minutes}m`;
}

interface BuildInitIncidentPayloadInput {
  computedIncident: ComputedItineraries;
  selectedFlights: SelectedFlights;
  cause: IncidentCause | "";
  consequence: IncidentConsequence;
}

/** Builds the postIncident body — mirrors InitIncident.initIncident (Vue), field for field. */
export function buildInitIncidentPayload({
  computedIncident,
  selectedFlights,
  cause,
  consequence,
}: BuildInitIncidentPayloadInput): InitIncidentPayload {
  const mapEntry = (entry: ComputedItineraryEntry) => ({
    flightId: entry.flightId,
    segmentId: entry.segmentId as number,
    referenceTicketLegId: entry.referenceTicketLegId as string,
    order: entry.order,
    sequence: entry.sequence,
    isImpactedSequence: entry.isImpactedSequence as boolean,
    lastInvoluntaryIndicator: entry.lastInvoluntaryIndicator,
    delta: entry.delta,
    isFlown: entry.isFlown,
  });

  return {
    incident: {
      source: "GDS",
      faultyFlightId: selectedFlights.faultyFlight?.id,
      impactedFlightId: selectedFlights.impactedFlight?.id,
      jurisdictionalFlightId: selectedFlights.jurisdictionalFlight?.id,
      cause: cause || "CANCELLED",
      consequence,
      isReturn: false,
      itinerary: {
        original: computedIncident.original
          .filter(({ isImpactedSequence }) => isImpactedSequence)
          .map(mapEntry),
        actual: computedIncident.actual.filter(({ isImpactedSequence }) => isImpactedSequence).map(mapEntry),
      },
    },
    claimTypeName: undefined,
    legalSpecification: { ec261CompensationAmount: undefined },
  };
}
